import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Font,
} from "@react-pdf/renderer";
import { parseDocument } from "htmlparser2";

// ---------------- FONT ----------------

Font.register({
  family: "Markazi Text",
  src: "/fonts/MarkaziText-Regular.ttf",
});

// ---------------- HELPERS ----------------
const isArabic = (text = "") => /[\u0600-\u06FF]/.test(text);
const removeDots = (text = "") => {
  if (!text) return text;
  let result = text;
  // Remove first dot if present
  if (result.startsWith(".")) {
    result = result.substring(1);
  }
  // Remove last dot if present
  // if (result.endsWith(".")) {
  //   result = result.slice(0, -1);
  // }
  return result;
};

// ---------------- HTML PARSER (NODE SAFE) ----------------
const parseHtmlToBlocks = (html = "") => {
  if (!html) return [];

  const dom = parseDocument(html);
  const blocks = [];

  const getText = (nodes = []) =>
    nodes
      .map((n) => {
        if (n.type === "text") return n.data;
        if (n.children) return getText(n.children);
        return "";
      })
      .join("")
      .trim();

  const walk = (nodes = []) => {
    nodes.forEach((node) => {
      // Paragraph
      if (node.name === "p") {
        blocks.push({ type: "p", text: getText(node.children) });
        return; // ⛔ stop here
      }

      // Line break
      if (node.name === "br") {
        blocks.push({ type: "br" });
        return;
      }

      // Unordered list
      if (node.name === "ul") {
        const items = node.children
          .filter((c) => c.name === "li")
          .map((li) => getText(li.children));
        blocks.push({ type: "ul", items });
        return; // ⛔ VERY IMPORTANT
      }

      // Ordered list
      if (node.name === "ol") {
        const items = node.children
          .filter((c) => c.name === "li")
          .map((li) => getText(li.children));
        blocks.push({ type: "ol", items });
        return; // ⛔ VERY IMPORTANT
      }

      // Plain text (outside lists)
      if (node.type === "text" && node.data.trim()) {
        blocks.push({ type: "text", text: node.data.trim() });
        return;
      }

      // Recurse only when safe
      if (node.children) {
        walk(node.children);
      }
    });
  };

  walk(dom.children);
  return blocks;
};


// ---------------- STYLES ----------------
const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 12,
    lineHeight: 1.5,
  },
  header: {
    fontSize: 22,
    marginBottom: 20,
    textAlign: "center",
    fontWeight: "bold",
  },
  content: {
    fontSize: 14,
    marginBottom: 15,
  },
  footer: {
    position: "absolute",
    bottom: 30,
    left: 30,
    right: 30,
    textAlign: "center",
    fontSize: 10,
  },
});

// ---------------- COMPONENT ----------------
const PDFGenerator = ({
  summary,
  title,
  pdfHeaderText,
  pdfFooterText,
}) => {
  const blocks = parseHtmlToBlocks(summary);

  const titleArabic = isArabic(title || pdfHeaderText);
  const footerArabic = isArabic(pdfFooterText);

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <Text
          style={{
            ...styles.header,
            fontFamily: titleArabic ? "Markazi Text" : "Helvetica",
            direction: titleArabic ? "rtl" : "ltr",
          }}
        >
          {title || pdfHeaderText}
        </Text>

        {/* Content */}
        <View style={styles.content}>
          {blocks.map((block, i) => {
            const combined =
              block.text || block.items?.join(" ") || "";

            const arabic = isArabic(combined);

            const textStyle = {
              fontFamily: arabic ? "Markazi Text" : "Helvetica",
              direction: arabic ? "rtl" : "ltr",
              textAlign: arabic ? "right" : "left",
              marginBottom: 8,
            };

            // Paragraph / text
            if (block.type === "p" || block.type === "text") {
              return (
                <Text key={i} style={textStyle}>
                  {arabic
                    ? removeDots(block.text)
                    : block.text}
                </Text>
              );
            }

            // Line break
            if (block.type === "br") {
              return <View key={i} style={{ height: 8 }} />;
            }

            // Unordered list
            if (block.type === "ul") {
              return (
                <View key={i} style={{ marginBottom: 10 }}>
                  {block.items.map((item, idx) => (
                    <Text key={idx} style={textStyle}>
                      • {arabic ? removeDots(item) : item}
                    </Text>
                  ))}
                </View>
              );
            }

            // Ordered list (EN numbers, RTL for Arabic)
            if (block.type === "ol") {
              return (
                <View key={i} style={{ marginBottom: 10 }}>
                  {block.items.map((item, idx) => (
                    <Text key={idx} style={textStyle}>
                      {arabic
                        ? `${idx + 1}. ${removeDots(item)}`
                        : `${idx + 1}. ${item}`}
                    </Text>
                  ))}
                </View>
              );
            }

            return null;
          })}
        </View>

        {/* Footer */}
        <Text
          style={{
            ...styles.footer,
            fontFamily: footerArabic ? "Markazi Text" : "Helvetica",
            direction: footerArabic ? "rtl" : "ltr",
          }}
        >
          {pdfFooterText}
        </Text>
      </Page>
    </Document>
  );
};

export default PDFGenerator;
